'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { MutableRefObject, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Badge } from '@flipster/react/design-system/badge';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Modal } from '@flipster/react/design-system/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { NrsusdtpCurrency } from '@flipster/utils/constants/currency';
import { formatEarnSunsetDisplayDate } from '@flipster/utils/earn-sunset';

import { CurrencyDisplay } from '../../../misc/display';
import { ModalErrorBoundary } from '../../../utils/modal-error-boundary';
import { earnNudgesBunja } from '../../nudges/state';
import { earnSunsetBunja } from '../bunja';
import GraphicEarnImage from './graphic-earn.png';

interface EarnSunsetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const EarnSunsetModal = ({ isOpen, onClose, onExit }: EarnSunsetModalProps) => {
  const { persistDismiss } = useBunja(earnSunsetBunja);
  const dontShowRef = useRef(false);

  const handleClose = () => {
    if (dontShowRef.current) persistDismiss();
    onClose();
  };

  return (
    <Modal.Root isOpen={isOpen} onClose={handleClose} onExit={onExit}>
      <ModalErrorBoundary onError={handleClose}>
        <EarnSunsetModalContent onClose={handleClose} dontShowRef={dontShowRef} />
      </ModalErrorBoundary>
    </Modal.Root>
  );
};

const EarnSunsetModalContent = ({
  onClose,
  dontShowRef,
}: {
  onClose: () => void;
  dontShowRef: MutableRefObject<boolean>;
}) => {
  const router = useRouter();
  const { narsilEstimatedAprAtom } = useBunja(earnNudgesBunja);
  const narsilEstimatedApr = useAtomValue(narsilEstimatedAprAtom);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const date = formatEarnSunsetDisplayDate();
  const highlightedDate = (
    <span key='date' className={css({ color: 'content.state.warning' })}>
      {date}
    </span>
  );

  const handleExplore = () => {
    onClose();
    router.push('/dynamic-earn/details/NRSUSDTP');
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Earn.r260827.Premium_earn_closing_title' />} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 16 })}>
          <p className={css({ textStyle: 'Body/L/Regular', color: 'content.default.level-3' })}>
            <FormattedMessage id='Earn.r260827.Sunset_modal_subtitle' values={{ date: highlightedDate }} />
          </p>
          <p className={css({ textStyle: 'Body/L/Strong', color: 'content.default.level-5' })}>
            <FormattedMessage id='Earn.r260827.To_keep_earning' />
          </p>
          <div
            className={hstack({
              px: 20,
              py: 12,
              rounded: 12,
              bg: 'background.default.level-1',
              justify: 'space-between',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-1',
              position: 'relative',
            })}
          >
            <div className={hstack({ gap: 20, flex: 1, minW: 0 })}>
              <CurrencyDisplay currency={NrsusdtpCurrency} icon={<CurrencyDisplay.Icon size={32} space={0} />} />
              <div className={stack({ gap: 4, align: 'flex-start' })}>
                <div className={hstack({ gap: 8 })}>
                  <p className={css({ textStyle: 'Body/XL/Regular', color: 'content.default.level-5' })}>
                    <FormattedMessage id='Autogen_common.r251126.s_Dynamic_Earn' />
                  </p>
                  <Badge type='tint' color='accent'>
                    <FormattedMessage id='Common.r260831.Live' />
                  </Badge>
                </div>
                <p className={css({ textStyle: 'Body/S/Regular', color: 'content.default.level-3' })}>
                  <FormattedMessage id='Earn.r260831.Narsil_usdt_prime' />
                </p>
                <div className={css({ h: 24 })}>
                  {narsilEstimatedApr != null && (
                    <p className={css({ textStyle: 'Body/XS/Strong', color: 'content.state.positive' })}>
                      <span className={css({ textStyle: 'Title/XS' })}>
                        <Formatted.Percent value={narsilEstimatedApr} isRate precision={0} toFixed />{' '}
                      </span>
                      <FormattedMessage id='Earn.r260304.Est_apr' />
                    </p>
                  )}
                </div>
              </div>
            </div>
            <img
              src={GraphicEarnImage.src}
              alt='Graphic Earn'
              className={css({ position: 'absolute', right: 0, bottom: 0 })}
              width={127}
              height={111}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer
        align='vertical'
        addon={
          <Checkbox
            checked={dontShowAgain}
            onChange={(event) => {
              const checked = event.currentTarget.checked;
              dontShowRef.current = checked;
              setDontShowAgain(checked);
            }}
            css={{ mb: 24 }}
          >
            <FormattedMessage id='Autogen_common.r251126.s_Dont_show_again' />
          </Checkbox>
        }
      >
        <Modal.Button
          hierarchy='secondary'
          color='neutral'
          size='large'
          label={<FormattedMessage id='Autogen_common.r251126.s_Got_it' />}
          onClick={onClose}
        />
        <Modal.Button
          size='large'
          label={<FormattedMessage id='Earn.r260831.Explore_dynamic_earn' />}
          onClick={handleExplore}
        />
      </Modal.Footer>
    </>
  );
};
